import pdfMake from 'addthaifont-pdfmake';
import vfsFonts from 'addthaifont-pdfmake/build/vfs_fonts';
import dayjs from 'dayjs';
import LOGO_URL from '../../assets/img/logo_sotus.png';
import SEAL_URL from '../../assets/img/seal_sotus.png';
import SIGNATURE_URL from '../../assets/img/signature_approver.png';

pdfMake.addVirtualFileSystem(vfsFonts);

pdfMake.fonts = {
  Sarabun: {
    normal: 'Sarabun-Light.ttf',
    bold: 'Sarabun-Regular.ttf',
    italics: 'Sarabun-LightItalic.ttf',
    bolditalics: 'Sarabun-Italic.ttf',
  },
};

const COMPANY = {
  nameTh: 'บริษัท โซตัส เอ็นจิเนียริ่ง จำกัด (สำนักงานใหญ่)',
  nameEn: 'SOTUS ENGINEERING CO., LTD.',
  address: '2244 ถนนปุณณกัณฑ์ ตำบลคอหงส์ อำเภอหาดใหญ่ จังหวัดสงขลา 90110',
  taxId: '0105557086951',
  phone: '086-2992930',
};

const BASE_COLOR = {
  text: '#111827',
  muted: '#4B5563',
  line: '#C9CDD3',
  softLine: '#DADDE3',
  light: '#FFFFFF',
};

const PAGE = {
  width: 595.28,
  marginX: 30,
  headerHeight: 210,
  footerHeight: 124,
  contentWidth: 519,
};

async function urlToBase64(url) {
  if (!url) return null;
  const response = await fetch(url);
  if (!response.ok) return null;
  const blob = await response.blob();
  if (!blob.type.startsWith('image/')) return null;
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function loadPdfImage(url) {
  try { return await urlToBase64(url); } catch { return null; }
}

function asNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function fmt(value) {
  return asNumber(value).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtQty(value) {
  return asNumber(value).toLocaleString('th-TH', { maximumFractionDigits: 2 });
}

function fmtDate(value) {
  return value ? dayjs(value).format('DD/MM/YYYY') : '';
}

function bahtText(amount) {
  const num = Math.max(0, asNumber(amount));
  const [intStr, decStr = '00'] = num.toFixed(2).split('.');
  const satang = parseInt(decStr.padEnd(2, '0').slice(0, 2), 10);
  const digits = ['', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
  const positions = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน'];
  const convertBlock = (block) => {
    const text = String(parseInt(block || '0', 10));
    if (text === '0') return '';
    let result = '';
    for (let i = 0; i < text.length; i += 1) {
      const digit = parseInt(text[i], 10);
      const pos = text.length - 1 - i;
      if (!digit) continue;
      if (pos === 1 && digit === 1) result += 'สิบ';
      else if (pos === 1 && digit === 2) result += 'ยี่สิบ';
      else if (pos === 0 && digit === 1 && text.length > 1 && text[text.length - 2] !== '0') result += 'เอ็ด';
      else result += digits[digit] + positions[pos];
    }
    return result;
  };
  const convertInteger = (val) => {
    if (val === '0') return 'ศูนย์';
    const groups = [];
    for (let end = val.length; end > 0; end -= 6) groups.unshift(val.slice(Math.max(0, end - 6), end));
    return groups.map((g, i) => {
      const t = convertBlock(g);
      return t ? t + (i < groups.length - 1 ? 'ล้าน' : '') : '';
    }).join('');
  };
  const baht = convertInteger(String(parseInt(intStr, 10))) + 'บาท';
  return satang === 0 ? `${baht}ถ้วน` : `${baht}${convertBlock(satang)}สตางค์`;
}

function getSnapshot(doc = {}) { return doc.snapshot || {}; }

function buildMetaTable(rows, C) {
  return {
    table: {
      widths: [58, '*'],
      body: rows.map(([label, value]) => [
        { text: label, fontSize: 8.5, bold: true, color: C.primary, border: [false, false, false, false], margin: [0, 0, 0, 0] },
        { text: value || '-', fontSize: 8.5, bold: true, color: C.text, border: [false, false, false, false], margin: [0, 0, 0, 0] },
      ]),
    },
    layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingTop: () => 0, paddingBottom: () => 0, paddingLeft: () => 0, paddingRight: () => 0 },
  };
}

function buildHeader(doc, logoB64, title, C) {
  const snap = getSnapshot(doc);
  const cm = 28.346;
  const isQuotation = doc.document_type === 'quotation';

  const revLabel = isQuotation ? `Rev ${String(doc.revision || 1).padStart(2, '0')}` : '';
  const rows1 = isQuotation
    ? [
        ['เลขที่', doc.document_number],
        ['วันที่', fmtDate(doc.date)],
        ['ผู้ขาย', doc.seller || '-'],
        ['อ้างอิง', revLabel],
      ]
    : [
        ['เลขที่', doc.document_number],
        ['วันที่', fmtDate(doc.date)],
        ['ครบกำหนด', fmtDate(doc.due_date)],
        ['อ้างอิง', doc.reference_text || '-'],
        ['ผู้ขาย', doc.seller || '-'],
      ];
  const rows2 = [
    ['ชื่องาน', doc.topic || snap.project_name],
    ['ผู้ติดต่อ', snap.contact_name],
    ['อีเมล', snap.contact_email],
    ['โทรศัพท์', snap.contact_phone],
  ].filter(([, v]) => v && String(v).trim());

  const abs = (node, xCm, yCm) => ({ ...node, absolutePosition: { x: xCm * cm, y: yCm * cm } });
  const absText = (text, xCm, yCm, width, opts = {}) => abs({
    columns: [{ width, text, fontSize: 8, color: C.text, ...opts }],
  }, xCm, yCm);
  const absLine = (xCm, yCm, lengthCm) => abs({
    canvas: [{ type: 'line', x1: 0, y1: 0, x2: lengthCm * cm, y2: 0, lineWidth: 0.5, lineColor: C.line }],
  }, xCm, yCm);

  const ADDR_W = 250;
  const LH = (8 * 1.25) / cm;
  // Sarabun Thai font in pdfMake renders cells taller than fontSize*lineHeight due to
  // ascent+descent metrics; empirically ~14pt per row at fontSize 8.5
  const META_LH = 14 / cm;
  const SP = 0.10;
  const ln = (text) => Math.max(1, Math.ceil((text || '').length / 62));

  const compNameY = 2.58;
  const compAddrY = compNameY + ln(COMPANY.nameTh) * LH + SP;
  const compTaxY = compAddrY + ln(COMPANY.address) * LH + SP;
  const compPhoneY = compTaxY + LH + SP;
  const compEndY = compPhoneY + LH;

  const sepY = compEndY + 0.30;
  const custLabelY = sepY - 0.08;
  const custNameY = sepY + 0.42;
  const custAddrY = custNameY + ln(snap.customer_name) * LH + SP;
  const custTaxY = custAddrY + ln(snap.customer_address) * LH + SP;

  const rows1EndY = 2.6 + rows1.length * META_LH;
  const rows2Y = isQuotation ? custNameY - 0.18 : rows1EndY + 0.40;
  const rightSepY = isQuotation ? sepY : rows1EndY + 0.18;

  return [
    abs({
      canvas: [{
        type: 'polyline',
        points: [{ x: 0, y: 0 }, { x: 58, y: 0 }, { x: 58, y: 58 }],
        color: C.primary,
        closePath: true,
        lineColor: C.primary,
        lineWidth: 0,
      }],
    }, 18.2, 0.42),
    abs(logoB64
      ? { image: logoB64, fit: [220, 48], margin: [0, 0, 0, 2] }
      : { stack: [{ text: COMPANY.nameEn, fontSize: 14, bold: true, color: C.text }, { text: 'Maintenance Solutions and Services', fontSize: 7.5, color: C.muted }] },
      1.25, 0.95),
    doc.document_type === 'billing_note' && doc.installment_number > 0
      ? abs({
          columns: [{
            width: 145,
            stack: [
              { text: title, fontSize: 14, color: C.primary, alignment: 'left' },
              { text: `งวดที่ ${doc.installment_number}`, fontSize: 9, bold: true, color: C.primary, alignment: 'left', margin: [0, 2, 0, 0] },
            ],
          }],
        }, 13, 1.3)
      : absText(title, 13, 1.3, 145, { fontSize: 14, color: C.primary, alignment: 'left' }),
    absLine(10.92, 2.38, 7.1),
    absText(COMPANY.nameTh, 1.25, compNameY, ADDR_W, { bold: true }),
    absText(COMPANY.address, 1.25, compAddrY, ADDR_W),
    absText(`เลขประจำตัวผู้เสียภาษี ${COMPANY.taxId}`, 1.25, compTaxY, ADDR_W),
    absText(`โทร. ${COMPANY.phone}`, 1.25, compPhoneY, 140),
    abs({ ...buildMetaTable(rows1, C), width: 214 }, 10.88, 2.6),
    absLine(10.88, rightSepY, 7.1),
    absText('ลูกค้า', 1.25, custLabelY, 80, { bold: true, color: C.primary }),
    absText(snap.customer_name || '-', 1.25, custNameY, ADDR_W, { bold: true }),
    snap.customer_address ? absText(snap.customer_address, 1.25, custAddrY, ADDR_W) : null,
    snap.customer_tax_id ? absText(`เลขประจำตัวผู้เสียภาษี ${snap.customer_tax_id}`, 1.25, custTaxY, ADDR_W) : null,
    abs({ ...buildMetaTable(rows2, C), width: 214 }, 10.88, rows2Y),
  ].filter(Boolean);
}

function getItemAmount(item) {
  const explicit = asNumber(item.amount);
  return explicit || asNumber(item.quantity) * asNumber(item.unit_price);
}

function buildItemsBody(items = [], C) {
  const headerCell = (text, alignment = 'left') => ({
    text, fontSize: 7.2, bold: true, color: C.text, fillColor: C.light, alignment, margin: [0, 1.5, 0, 1.5],
  });

  const rows = items.map((item, index) => {
    const details = String(item.description_detail || '').split('\n').map((l) => l.trim()).filter(Boolean);
    const detailNodes = details.flatMap((line) => [{ text: '\n' }, { text: line, fontSize: 7.2, color: C.text }]);
    return [
      { text: index + 1, alignment: 'center', fontSize: 7.5, color: C.text },
      { text: [{ text: item.description || '-', fontSize: 7.6, bold: true, color: C.text }, ...detailNodes] },
      { text: fmtQty(item.quantity), alignment: 'right', fontSize: 7.5, color: C.text },
      { text: item.unit || '', alignment: 'center', fontSize: 7.5, color: C.text },
      { text: fmt(item.unit_price), alignment: 'right', fontSize: 7.5, color: C.text },
      { text: fmt(getItemAmount(item)), alignment: 'right', fontSize: 7.5, color: C.text },
    ];
  });

  return [
    [headerCell('#', 'center'), headerCell('รายละเอียด'), headerCell('จำนวน', 'right'), headerCell('หน่วย', 'center'), headerCell('ราคาต่อหน่วย', 'right'), headerCell('ยอดรวม', 'right')],
    ...(rows.length ? rows : [[{ text: '-', colSpan: 6, alignment: 'center', color: C.muted, fontSize: 9, margin: [0, 8, 0, 8] }, {}, {}, {}, {}, {}]]),
  ];
}

function calculateSummary(doc = {}) {
  const items = Array.isArray(doc.items) ? doc.items : [];
  const subtotal = items.reduce((s, item) => s + getItemAmount(item), 0);
  const rawDiscount = Math.max(0, asNumber(doc.discount));
  const discountAmount = doc.discountType === '%'
    ? subtotal * (Math.min(rawDiscount, 100) / 100)
    : Math.min(rawDiscount, subtotal);
  const afterDiscount = Math.max(0, subtotal - discountAmount);
  const vatRate = asNumber(doc.vat_rate) || 7;
  const vatAmount = doc.include_vat ? 0 : afterDiscount * (vatRate / 100);
  return { subtotal, discountAmount, afterDiscount, vatRate, vatAmount, total: afterDiscount + vatAmount };
}

function buildSummaryRows(summary, includeVat, C) {
  const cell = (text, opts = {}) => ({ text, fontSize: 7.5, border: [false, false, false, false], margin: [0, 1.5, 0, 1.5], ...opts });
  const rows = [[
    cell('รวมเป็นเงิน', { color: C.primary, alignment: 'right' }),
    cell(`${fmt(summary.subtotal)} บาท`, { alignment: 'right' }),
  ]];
  if (summary.discountAmount > 0) {
    rows.push([cell('ส่วนลด', { color: C.primary, alignment: 'right' }), cell(`-${fmt(summary.discountAmount)} บาท`, { alignment: 'right' })]);
    rows.push([cell('ยอดหลังหักส่วนลด', { color: C.primary, alignment: 'right' }), cell(`${fmt(summary.afterDiscount)} บาท`, { alignment: 'right' })]);
  }
  rows.push([
    cell(includeVat ? `VAT ${summary.vatRate}% (รวมในราคาแล้ว)` : `VAT ${summary.vatRate}%`, { color: C.primary, alignment: 'right' }),
    cell(`${fmt(summary.vatAmount)} บาท`, { alignment: 'right' }),
  ]);
  rows.push([
    cell('จำนวนเงินรวมทั้งสิ้น', { bold: true, alignment: 'right', color: C.primary, border: [false, true, false, false], margin: [0, 3, 0, 1] }),
    cell(`${fmt(summary.total)} บาท`, { bold: true, alignment: 'right', color: C.text, border: [false, true, false, false], margin: [0, 3, 0, 1] }),
  ]);
  return rows;
}

function buildNotes(notes, C) {
  if (!notes) return null;
  return {
    margin: [0, 18, 0, 0],
    stack: [
      { text: 'หมายเหตุ', fontSize: 7.5, bold: true, color: C.primary, margin: [0, 0, 0, 3] },
      { text: notes, fontSize: 7.2, color: C.text, lineHeight: 1.2 },
    ],
  };
}

function buildPaymentTerms(paymentTerms, C) {
  if (!paymentTerms?.trim()) return null;
  return {
    margin: [0, 14, 0, 0],
    stack: [
      { text: 'เงื่อนไขการจ่ายเงิน', fontSize: 7.5, bold: true, color: C.primary, margin: [0, 0, 0, 3] },
      { text: paymentTerms, fontSize: 7.2, color: C.text, lineHeight: 1.2 },
    ],
  };
}

function computeTopMargin(doc) {
  const cm = 28.346;
  const LH = (8 * 1.25) / cm;
  const META_LH = 14 / cm;
  const SP = 0.10;
  const ln = (text) => Math.max(1, Math.ceil((text || '').length / 62));

  const compNameY = 2.58;
  const compAddrY = compNameY + ln(COMPANY.nameTh) * LH + SP;
  const compTaxY  = compAddrY + ln(COMPANY.address) * LH + SP;
  const compPhoneY = compTaxY + LH + SP;
  const compEndY   = compPhoneY + LH;
  const sepY       = compEndY + 0.30;
  const custNameY  = sepY + 0.42;

  const snap = getSnapshot(doc);
  const isQuotation = doc.document_type === 'quotation';
  const rows1Count = isQuotation ? 4 : 5;
  const rows2Count = [
    doc.topic || snap.project_name,
    snap.contact_name,
    snap.contact_email,
    snap.contact_phone,
  ].filter((v) => v && String(v).trim()).length;
  const rows1EndY = 2.6 + rows1Count * META_LH;
  const rows2Y = isQuotation ? custNameY - 0.18 : rows1EndY + 0.40;
  const rightColEndPt = (rows2Y + Math.max(1, rows2Count) * META_LH) * cm + 5;

  return Math.max(PAGE.headerHeight, rightColEndPt);
}

function buildTerms(doc, summary, C) {
  if (!doc.terms_conditions?.trim()) return null;

  const validUntil = doc.due_date ? fmtDate(doc.due_date) : null;
  const creditDays = doc.snapshot?.credit_days || 30;
  const vatText = doc.include_vat
    ? `ราคาตามใบเสนอราคานี้รวม VAT ${summary.vatRate}% แล้ว`
    : `ราคาตามใบเสนอราคานี้ยังไม่รวม VAT ${summary.vatRate}% โดยคำนวณเพิ่มในยอดรวม`;

  const customLines = doc.terms_conditions.split('\n').filter((l) => l.trim());
  const rows = [
    ...(validUntil ? [`ระยะเวลายืนราคา ถึงวันที่ ${validUntil}`] : []),
    `Term Payment — Credit ${creditDays} Days`,
    ...customLines,
    vatText,
  ];

  return {
    margin: [0, 16, 0, 0],
    stack: [
      { text: 'เงื่อนไขการเสนอราคา', fontSize: 7.5, bold: true, color: C.primary, margin: [0, 0, 0, 3] },
      {
        table: {
          widths: [14, '*'],
          body: rows.map((text, index) => [
            { text: `${index + 1})`, fontSize: 7.2, bold: true, color: C.text },
            { text, fontSize: 7.2, color: C.text },
          ]),
        },
        layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingTop: () => 1, paddingBottom: () => 1, paddingLeft: () => 0, paddingRight: () => 0 },
      },
    ],
  };
}

function buildFooter(doc, currentPage, pageCount, sealB64, signatureB64, C) {
  const isLastPage = currentPage === pageCount;
  const snap = getSnapshot(doc);
  const stack = [];
  const line = (width = 88) => ({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: width, y2: 0, lineWidth: 0.5, lineColor: C.line }] });
  const empty = (colSpan = 1) => ({ text: '', colSpan, border: [false, false, false, false] });
  const cell = (content, opts = {}) => ({ border: [false, false, false, false], margin: [0, 0, 0, 0], ...(typeof content === 'string' ? { text: content } : content), ...opts });
  const label = (text) => cell(text, { fontSize: 7.2, bold: true, color: C.text, alignment: 'center' });

  if (isLastPage) {
    const dateStr = fmtDate(doc.date);
    stack.push({
      margin: [31, 0, 0, 0],
      table: {
        widths: [88, 11, 88, 132, 88, 1, 88, 23],
        body: [
          [cell(`ในนาม ${snap.customer_name || ''}`, { colSpan: 3, fontSize: 7.2, bold: true, color: C.text }), {}, {}, empty(), cell(`ในนาม ${COMPANY.nameTh.replace(' (สำนักงานใหญ่)', '')}`, { colSpan: 4, fontSize: 7.2, bold: true, color: C.text, alignment: 'center' }), {}, {}, {}],
          [empty(8), {}, {}, {}, {}, {}, {}, {}],
          [empty(), empty(), empty(), sealB64 ? cell({ image: sealB64, fit: [110, 34], alignment: 'center' }) : empty(), empty(), empty(), empty(), empty()],
          [empty(), empty(), empty(), empty(), signatureB64 ? cell({ image: signatureB64, fit: [88, 24], alignment: 'center' }, { margin: [0, -2, 0, -2] }) : empty(), empty(), cell(dateStr, { fontSize: 7.2, bold: true, color: C.text, alignment: 'center', margin: [0, 10, 0, 0] }), empty()],
          [cell(line()), empty(), cell(line()), empty(), cell(line()), empty(), cell(line()), empty()],
          [label('ผู้สั่งซื้อสินค้า'), empty(), label('วันที่'), empty(), label('ผู้อนุมัติ'), empty(), label('วันที่'), empty()],
        ],
      },
      layout: 'noBorders',
    });
  }
  stack.push({ text: `หน้าที่ ${currentPage}/${pageCount}`, fontSize: 7.2, color: C.text, margin: [31, isLastPage ? 16 : 96, 0, 0] });
  return { stack };
}

export function defaultFilename(doc) {
  const docDate = dayjs(doc.date || new Date());
  const projNum = doc.project_id?.project_number || doc.snapshot?.project_number || '';
  const contactId = doc.client_id?.contact_id || doc.snapshot?.contact_id || '';
  const docNum = doc.document_number || 'draft';
  const datePart = docDate.format('YYYY-DD-MM');
  return [datePart, projNum, contactId, docNum].filter(Boolean).join('-') + '.pdf';
}

export function createDocumentPDFGenerator(title, primaryColor, getFilename = defaultFilename) {
  const C = { ...BASE_COLOR, primary: primaryColor };

  return async (doc = {}, preOpenedWin = null) => {
    let logoB64;
    let sealB64;
    let signatureB64;
    try {
      [logoB64, sealB64, signatureB64] = await Promise.all([
        loadPdfImage(LOGO_URL),
        loadPdfImage(SEAL_URL),
        loadPdfImage(SIGNATURE_URL),
      ]);
    } catch (imgErr) {
      console.warn('[PDF] image load error (continuing):', imgErr);
    }
    const items = Array.isArray(doc.items) ? doc.items : [];
    const summary = calculateSummary(doc);
    const summaryRows = buildSummaryRows(summary, doc.include_vat, C);

    const filename = getFilename(doc);

    const docDefinition = {
      info: { title: filename },
      pageSize: 'A4',
      pageOrientation: 'Portrait',
      pageMargins: [PAGE.marginX, computeTopMargin(doc), PAGE.marginX, PAGE.footerHeight],
      defaultStyle: { font: 'Sarabun', fontSize: 9, color: C.text, lineHeight: 1.25 },
      header: () => buildHeader(doc, logoB64, title, C),
      footer: (currentPage, pageCount) => buildFooter(doc, currentPage, pageCount, sealB64, signatureB64, C),
      content: [
        {
          table: { headerRows: 1, dontBreakRows: false, widths: [22, 200, 52, 44, 78, 82], body: buildItemsBody(items, C) },
          layout: { hLineWidth: () => 0.5, vLineWidth: () => 0, hLineColor: () => C.softLine, paddingLeft: (i) => (i === 0 ? 0 : 5), paddingRight: () => 5, paddingTop: () => 3, paddingBottom: () => 3 },
        },
        {
          margin: [0, 14, 0, 0],
          unbreakable: true,
          columns: [
            {
              width: '*',
              stack: [
                { text: 'จำนวนเงินเป็นตัวอักษร', fontSize: 8, color: C.muted, margin: [0, 4, 0, 3] },
                { text: `(${bahtText(summary.total)})`, fontSize: 9, bold: true, color: C.text },
              ],
            },
            {
              width: 268,
              table: { widths: ['*', 120], body: summaryRows },
              layout: { hLineWidth: () => 0, vLineWidth: () => 0, hLineColor: () => C.line, paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 },
            },
          ],
        },
        buildNotes(doc.notes, C),
        buildPaymentTerms(doc.payment_terms, C),
        buildTerms(doc, summary, C),
      ].filter(Boolean),
    };

    try {
      const blob = await pdfMake.createPdf(docDefinition).getBlob();
      const url = URL.createObjectURL(blob);
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${filename}</title><style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:sans-serif;background:#404040;display:flex;flex-direction:column;height:100vh}.toolbar{background:#323232;color:#fff;padding:8px 16px;display:flex;align-items:center;gap:12px;flex-shrink:0}.filename{font-size:13px;opacity:.85;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.btn{background:#1976d2;color:#fff;border:none;border-radius:4px;padding:6px 14px;font-size:13px;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;gap:6px;flex-shrink:0}.btn:hover{background:#1565c0}iframe{flex:1;border:none;width:100%}</style></head><body><div class="toolbar"><span class="filename">&#128196; ${filename}</span><a class="btn" href="${url}" download="${filename}">&#11015; ดาวโหลด</a></div><iframe src="${url}"></iframe></body></html>`;
      const htmlBlob = new Blob([html], { type: 'text/html' });
      const htmlUrl = URL.createObjectURL(htmlBlob);
      const win = (preOpenedWin && !preOpenedWin.closed) ? preOpenedWin : window.open('', '_blank');
      if (win) win.location.href = htmlUrl;
      setTimeout(() => { URL.revokeObjectURL(url); URL.revokeObjectURL(htmlUrl); }, 300000);
      setTimeout(() => URL.revokeObjectURL(url), 300000);
    } catch (error) {
      console.error('[PDF] Error generating PDF:', error);
      throw new Error(`ไม่สามารถสร้าง PDF ได้: ${error.message}`);
    }
  };
}
